import { useTheme } from '../../../../theme';
import { type CSSObject } from '@emotion/react';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { paperClasses } from '@mui/material/Paper';
import { touchRippleClasses } from '@mui/material/ButtonBase';
import {
  dateCalendarClasses,
  dayCalendarClasses,
  monthCalendarClasses,
  pickersCalendarHeaderClasses,
  pickersDayClasses,
  pickersMonthClasses,
  pickersYearClasses,
} from '@mui/x-date-pickers';
import { pickersArrowSwitcherClasses } from '@mui/x-date-pickers/internals';

type Return = Record<
  | 'popper'
  | 'calendarHeader'
  | 'calendarDayLabelsRow'
  | 'calendarDayLabels'
  | 'calendarDayValuesRow'
  | 'calendarDayValues'
  | 'calendarYearValues'
  | 'calendarMonthValues'
  | 'icons'
  | 'input',
  CSSObject
>;

export const useDateInputStyles = (): Return => {
  const theme = useTheme();

  const disableRipple = {
    [`.${touchRippleClasses.root}`]: {
      display: 'none',
    },
  };

  return {
    popper: {
      [`.${paperClasses.root}`]: {
        borderRadius: theme.shape.radius12,
        border: `1px solid ${theme.datepicker.base.border}`,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.datepicker.base.background,
        marginTop: theme.spacing(1),
      },
      [`.${dateCalendarClasses.root}`]: {
        marginBottom: '16px',
      },
    },
    calendarHeader: {
      [`&.${pickersCalendarHeaderClasses.root}`]: {
        marginBottom: '10px',
      },
      [`& .${pickersCalendarHeaderClasses.label}`]: {
        ...theme.typography.titleH4,
        color: theme.text.generic.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
      },
      [`& .${pickersCalendarHeaderClasses.switchViewButton}`]: {
        ...disableRipple,
        '&:hover': {
          backgroundColor: theme.datepicker.cells.hover,
          borderRadius: theme.shape.radius8,
        },
      },
      [`& .${pickersArrowSwitcherClasses.root}`]: {
        paddingRight: '6px',
      },
      [`& .${pickersArrowSwitcherClasses.spacer}`]: {
        width: '48px',
      },
      [`& .${pickersArrowSwitcherClasses.button}`]: {
        ...disableRipple,
        padding: 0,
        '&:hover': {
          backgroundColor: theme.datepicker.cells.hover,
          borderRadius: theme.shape.radius8,
        },
      },
    },
    calendarDayLabelsRow: {
      [`& .${dayCalendarClasses.header}`]: {
        margin: '0 26px 16px 26px',
      },
    },
    calendarDayLabels: {
      [`& .${dayCalendarClasses.weekDayLabel}`]: {
        color: theme.text.generic.tertiary,
        width: '36px',
        height: '36px',
      },
    },
    calendarDayValuesRow: {
      [`& .${dayCalendarClasses.weekContainer}`]: {
        margin: `0 28px 2px 28px`,
      },
    },
    calendarDayValues: {
      [`& .${pickersDayClasses.root}`]: {
        color: theme.text.generic.primary,
        backgroundColor: theme.datepicker.cells.default,
        ...theme.typography.bodyS,
        borderRadius: theme.shape.radius8,
      },
      [`& .${dayCalendarClasses.weekContainer} .${pickersDayClasses.root}:hover`]:
        {
          backgroundColor: theme.datepicker.cells.hover,
          color: theme.text.generic.primary,
          fontWeight: theme.typography.fontWeightSemiBold,
          ...disableRipple,
        },
      [`& .${dayCalendarClasses.weekContainer} .${pickersDayClasses.root}.Mui-disabled`]:
        {
          color: theme.text.interactive.disabled,
        },
      [`& .${dayCalendarClasses.weekContainer} .${pickersDayClasses.root}.Mui-selected`]:
        {
          borderRadius: theme.shape.radius8,
          backgroundColor: theme.datepicker.cells.selected,
          color: theme.text.generic.primary,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
      [`& .${dayCalendarClasses.weekContainer} .${pickersDayClasses.root}.${pickersDayClasses.today}`]:
        {
          border: `1px solid ${theme.datepicker.cells.todayborder}`,
          borderRadius: theme.shape.radius8,
        },
      [`& .${pickersDayClasses.dayOutsideMonth}`]: {
        color: theme.text.interactive.disabled,
      },
    },
    calendarYearValues: {
      [`& .${pickersYearClasses.root}`]: {
        color: theme.text.generic.secondary,
        backgroundColor: theme.datepicker.cells.default,
      },
      [`& .${pickersYearClasses.root} .${pickersYearClasses.yearButton}`]: {
        ...theme.typography.bodyS,
      },
      [`&. ${pickersYearClasses.root} .${pickersYearClasses.yearButton}:hover[aria-current="date"]`]:
        {
          border: `1px solid ${theme.datepicker.cells.todayborder}`,
          borderRadius: theme.shape.radius8,
        },
      [`& .${pickersYearClasses.root} .${pickersYearClasses.yearButton}.Mui-disabled`]:
        {
          color: theme.text.interactive.disabled,
        },
      [`& .${pickersYearClasses.root} .${pickersYearClasses.yearButton}.Mui-selected`]:
        {
          borderRadius: theme.shape.radius8,
          backgroundColor: theme.datepicker.cells.selected,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
      [`& .${pickersYearClasses.root} .${pickersYearClasses.yearButton}:hover`]:
        {
          backgroundColor: theme.datepicker.cells.hover,
          borderRadius: theme.shape.radius8,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
    },
    calendarMonthValues: {
      [`& .${monthCalendarClasses.root}`]: {
        color: theme.text.generic.secondary,
        backgroundColor: theme.datepicker.cells.default,
      },
      [`& .${monthCalendarClasses.root} .${pickersMonthClasses.monthButton}`]: {
        ...theme.typography.bodyS,
      },
      [`& .${pickersMonthClasses.root} .${pickersMonthClasses.monthButton}[aria-current="date"]`]:
        {
          borderRadius: theme.shape.radius8,
        },
      [`& .${pickersMonthClasses.root} .${pickersMonthClasses.monthButton}.Mui-disabled`]:
        {
          color: theme.text.interactive.disabled,
        },
      [`& .${pickersMonthClasses.root} .${pickersMonthClasses.monthButton}.Mui-selected`]:
        {
          borderRadius: theme.shape.radius8,
          backgroundColor: theme.datepicker.cells.selected,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
      [`& .${pickersMonthClasses.root} .${pickersMonthClasses.monthButton}:hover`]:
        {
          backgroundColor: theme.datepicker.cells.hover,
          borderRadius: theme.shape.radius8,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
    },
    icons: {
      // we clear out any margins MUI adds as we set our own
      [`& .${inputAdornmentClasses.root}.${inputAdornmentClasses.positionEnd}`]:
        {
          margin: theme.spacing(0),
        },
      '& .clearButton': {
        // The MUI clear button is hidden by default, and only shown when the input is focused
        opacity: 1,
      },
    },
    input: {
      [`& .${inputBaseClasses.root}`]: {
        // There is not enough specificity to override base styles
        gap: '0 !important',

        [`& .${inputBaseClasses.input}`]: {
          textTransform: 'lowercase',
          padding: theme.spacing(0, 2),
        },
      },
    },
  };
};
